import { supabase } from '../lib/supabase';
import { signUp } from './authService';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Get all users with their profiles
export const getAllUsers = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching users:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Error fetching users:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching users:', error?.message);
    return [];
  }
};

// Get user by ID
export const getUserById = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching user:', error?.message);
        throw error;
      }
      console.log('Error fetching user:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching user:', error?.message);
    return null;
  }
};

// Get current user profile
export const getCurrentUserProfile = async () => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      return null;
    }

    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching profile:', error?.message);
        throw error;
      }
      console.log('Error fetching profile:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching profile:', error?.message);
    return null;
  }
};

// Create new user (admin only)
export const createUser = async (userData) => {
  try {
    const { email, password, fullName, role, screenPermissions, featurePermissions } = userData;

    // Create auth user with metadata
    const { data: authData, error: authError } = await signUp(email, password, fullName, role);

    if (authError) {
      throw authError;
    }

    const userId = authData?.user?.id;

    if (!userId) {
      throw new Error('Failed to create user');
    }

    // Get current user for granted_by
    const { data: { user: currentUser } } = await supabase?.auth?.getUser();

    // Add screen permissions
    if (screenPermissions && screenPermissions?.length > 0) {
      const screenPerms = screenPermissions?.map(screenId => ({
        user_id: userId,
        screen_id: screenId,
        granted_by: currentUser?.id
      }));

      const { error: screenError } = await supabase?.from('user_screen_permissions')?.insert(screenPerms);

      if (screenError && !isSchemaError(screenError)) {
        console.log('Error adding screen permissions:', screenError?.message);
      }
    }

    // Add feature permissions
    if (featurePermissions && featurePermissions?.length > 0) {
      const featurePerms = featurePermissions?.map(featureId => ({
        user_id: userId,
        feature_id: featureId,
        granted_by: currentUser?.id
      }));

      const { error: featureError } = await supabase?.from('user_feature_permissions')?.insert(featurePerms);

      if (featureError && !isSchemaError(featureError)) {
        console.log('Error adding feature permissions:', featureError?.message);
      }
    }

    return await getUserById(userId);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    throw new Error(error.message || 'Failed to create user');
  }
};

// Update user profile
export const updateUserProfile = async (userId, updates) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Delete user (admin only)
export const deleteUser = async (userId) => {
  try {
    // Deleting from user_profiles will cascade to auth.users
    const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error deleting user:', error?.message);
        throw error;
      }
      throw new Error(error.message);
    }

    return true;
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    throw new Error(error.message || 'Failed to delete user');
  }
};

// Get all screens
export const getAllScreens = async () => {
  try {
    const { data, error } = await supabase?.from('screens')?.select('*')?.order('display_name', { ascending: true });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching screens:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Error fetching screens:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching screens:', error?.message);
    return [];
  }
};

// Get all features
export const getAllFeatures = async () => {
  try {
    const { data, error } = await supabase?.from('features')?.select('*')?.order('display_name', { ascending: true });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching features:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Error fetching features:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching features:', error?.message);
    return [];
  }
};

// Get user screen permissions
export const getUserScreenPermissions = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_screen_permissions')?.select('screen_id, screens(*)')?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching screen permissions:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Error fetching screen permissions:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching screen permissions:', error?.message);
    return [];
  }
};

// Get user feature permissions
export const getUserFeaturePermissions = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_feature_permissions')?.select('feature_id, features(*)')?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error fetching feature permissions:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') {
        return [];
      }
      console.log('Error fetching feature permissions:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.log('Error fetching feature permissions:', error?.message);
    return [];
  }
};

// Update user permissions
export const updateUserPermissions = async (userId, screenPermissions, featurePermissions) => {
  try {
    const { data: { user: currentUser } } = await supabase?.auth?.getUser();

    // Delete existing permissions
    await supabase?.from('user_screen_permissions')?.delete()?.eq('user_id', userId);
    await supabase?.from('user_feature_permissions')?.delete()?.eq('user_id', userId);

    // Add new screen permissions
    if (screenPermissions && screenPermissions?.length > 0) {
      const screenPerms = screenPermissions?.map(screenId => ({
        user_id: userId,
        screen_id: screenId,
        granted_by: currentUser?.id
      }));

      await supabase?.from('user_screen_permissions')?.insert(screenPerms);
    }

    // Add new feature permissions
    if (featurePermissions && featurePermissions?.length > 0) {
      const featurePerms = featurePermissions?.map(featureId => ({
        user_id: userId,
        feature_id: featureId,
        granted_by: currentUser?.id
      }));

      await supabase?.from('user_feature_permissions')?.insert(featurePerms);
    }

    return true;
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    throw new Error(error.message || 'Failed to update permissions');
  }
};